import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import useLenis from "@/lib/useLenis";
import useSEO, { useJsonLd } from "@/lib/useSEO";
import useContent from "@/lib/useContent";
import Nav from "@/sections/Nav";
import Footer from "@/sections/Footer";
import { Toaster } from "@/components/ui/sonner";
import NotFound from "@/pages/NotFound";
import { JOURNEYS } from "@/lib/packages-data";
import { CONTACT, SITE_URL } from "@/lib/site-data";
import { ARTICLES, JOURNAL_PATH, JOURNAL_AUTHOR } from "@/lib/journal-data";

const EASE = [0.76, 0, 0.24, 1];
const WA = `https://wa.me/${CONTACT.phone1.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
  "Kuzuzangpo la! I'd like to plan a journey to Bhutan with Planet Air Adventure."
)}`;
const fmtDate = (d) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

function Shell({ children }) {
  return (
    <div className="App relative">
      <div className="grain" aria-hidden />
      <Nav />
      <main>{children}</main>
      <Footer />
      <Toaster />
    </div>
  );
}

function ArticleCard({ a }) {
  return (
    <Link to={`${JOURNAL_PATH}/${a.slug}`} className="group block" data-testid={`journal-card-${a.slug}`}>
      <div className="relative overflow-hidden aspect-[4/3] bg-foreground/5">
        <img
          src={a.img}
          alt={a.imgAlt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-white">
          <span className="bg-[#D1492E] px-2.5 py-1">{a.tag}</span>
          <span>{a.read}</span>
        </div>
      </div>
      <h3 className="mt-5 font-serif text-2xl md:text-3xl leading-tight font-light group-hover:text-primary transition-colors">{a.title}</h3>
      <p className="mt-3 text-sm md:text-base text-foreground/65 font-light leading-relaxed">{a.excerpt}</p>
    </Link>
  );
}

export function JournalIndex() {
  useLenis();
  useSEO({
    title: "Bhutan Travel Guide · Journal",
    description:
      "Practical Bhutan travel guides from our team in Paro: the best time to visit, visa requirements, the SDF, how to get to Bhutan and trekking advice.",
    path: JOURNAL_PATH,
  });
  useJsonLd("breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}${JOURNAL_PATH}` },
    ],
  });
  return (
    <Shell>
      <section className="pt-40 md:pt-48 pb-24 md:pb-32 bg-background">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-foreground/60 mb-6 flex items-center gap-3">
            <span className="w-6 h-px bg-primary" />
            Journal · Field notes from Paro
          </div>
          <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.94] tracking-tighter max-w-5xl">
            Bhutan travel guide, <em className="italic text-primary">from the ground.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-foreground/65 font-light leading-relaxed">
            Straight answers to the questions travellers ask us every week — written by our curators in Paro and kept up to date.
          </p>
          <div className="mt-16 md:mt-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {ARTICLES.map((a) => (
              <ArticleCard key={a.slug} a={a} />
            ))}
          </div>
        </div>
      </section>
    </Shell>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const a = ARTICLES.find((x) => x.slug === slug);
  return a ? <ArticleView key={a.slug} a={a} /> : <NotFound />;
}

function ArticleView({ a }) {
  useLenis();
  const journeys = useContent("journeys", JOURNEYS);
  const url = `${SITE_URL}${JOURNAL_PATH}/${a.slug}`;

  useSEO({ title: a.seoTitle, description: a.description, path: `${JOURNAL_PATH}/${a.slug}`, image: a.img });
  // ids match scripts/prerender.mjs so the pre-built schema is replaced, not duplicated
  useJsonLd("article", [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: a.title,
        description: a.description,
        image: a.img,
        datePublished: a.published,
        dateModified: a.updated,
        author: { "@type": "Organization", name: JOURNAL_AUTHOR, url: SITE_URL },
        publisher: { "@type": "Organization", name: "Planet Air Adventure", url: SITE_URL },
        mainEntityOfPage: url,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: a.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}${JOURNAL_PATH}` },
          { "@type": "ListItem", position: 3, name: a.title, item: url },
        ],
      },
  ]);
  const related = (a.related || []).map((s) => journeys.find((j) => j.slug === s)).filter(Boolean);
  const more = ARTICLES.filter((x) => x.slug !== a.slug).slice(0, 3);

  return (
    <Shell>
      <article>
        <header className="relative bg-[#0A0B0A] text-[#F5F2EB] overflow-hidden">
          <img src={a.img} alt={a.imgAlt} className="absolute inset-0 w-full h-full object-cover opacity-45" draggable="false" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0A] via-[#0A0B0A]/40 to-[#0A0B0A]/50" />
          <div className="relative mx-auto max-w-[1000px] px-6 md:px-10 pt-44 md:pt-56 pb-16 md:pb-24">
            <nav aria-label="Breadcrumb" className="text-[10px] uppercase tracking-[0.28em] text-[#F5F2EB]/60 mb-8">
              <Link to="/" className="hover:text-[#D1492E]">Home</Link> ›{" "}
              <Link to={JOURNAL_PATH} className="hover:text-[#D1492E]">Journal</Link>
            </nav>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE }}
              className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tighter"
            >
              {a.title}
            </motion.h1>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.24em] text-[#F5F2EB]/65">
              <span className="text-[#D1492E]">{a.tag}</span>
              <span>{a.read}</span>
              <span>Updated {fmtDate(a.updated)}</span>
              <span>By {JOURNAL_AUTHOR}</span>
            </div>
          </div>
        </header>

        <div className="bg-background">
          <div className="mx-auto max-w-[760px] px-6 md:px-10 py-16 md:py-24">
            <div className="border-l-2 border-primary pl-6 md:pl-8 py-1" data-testid="article-answer">
              <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-3">Short answer</div>
              <p className="font-serif text-xl md:text-2xl leading-snug tracking-tight">{a.answer}</p>
            </div>

            {a.sections.map((s) => (
              <section key={s.h} className="mt-14">
                <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight leading-tight">{s.h}</h2>
                {(s.paras || []).map((p) => (
                  <p key={p.slice(0, 40)} className="mt-5 text-base md:text-lg font-light text-foreground/80 leading-relaxed">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-6 space-y-3">
                    {s.list.map((x) => (
                      <li key={x} className="flex gap-4 text-base md:text-lg font-light text-foreground/80 leading-relaxed">
                        <span className="mt-3 w-1.5 h-1.5 bg-primary rotate-45 shrink-0" />
                        {x}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {a.links && (
              <div className="mt-10 flex flex-wrap gap-6 text-[11px] uppercase tracking-[0.22em] font-medium">
                {a.links.map((l) => (
                  <Link key={l.href} to={l.href} className="text-primary hover:underline">{l.label} →</Link>
                ))}
              </div>
            )}

            <section className="mt-16 border-t border-foreground/15 pt-12">
              <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight">Frequently asked questions</h2>
              <div className="mt-6 divide-y divide-foreground/15">
                {a.faqs.map((f) => (
                  <div key={f.q} className="py-6">
                    <h3 className="font-serif text-xl md:text-2xl tracking-tight">{f.q}</h3>
                    <p className="mt-3 text-base font-light text-foreground/75 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-12 bg-[#0A0B0A] text-[#F5F2EB] p-8 md:p-12">
              <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight">Plan it with us in Paro</h2>
              <p className="mt-4 text-base font-light text-[#F5F2EB]/75 leading-relaxed">
                Visa, SDF, guide, vehicle and hotels handled by our own team. Send your dates and a curator replies within 24 hours.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center">
                <a
                  href={WA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-sharp inline-flex items-center gap-3 px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-medium border border-[#F5F2EB]"
                >
                  WhatsApp {CONTACT.phone1} <span aria-hidden>→</span>
                </a>
                <Link to="/journeys" className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/80 hover:text-[#D1492E]">
                  Browse journeys
                </Link>
              </div>
            </section>
          </div>

          {related.length > 0 && (
            <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-16">
              <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight">Journeys for this trip</h2>
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {related.map((j) => (
                  <Link key={j.slug} to={`/journeys/${j.slug}`} className="group block border-t border-foreground/20 pt-5">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-foreground/55">{[j.duration, j.category].filter(Boolean).join(" · ")}</div>
                    <div className="mt-2 font-serif text-2xl tracking-tight group-hover:text-primary transition-colors">{j.title}</div>
                    {j.subtitle && <div className="mt-1 text-sm text-foreground/60">{j.subtitle}</div>}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24 md:pb-32">
            <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight">More from the Journal</h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {more.map((m) => (
                <ArticleCard key={m.slug} a={m} />
              ))}
            </div>
          </section>
        </div>
      </article>
    </Shell>
  );
}
